import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { App, Alert, Button, Descriptions, Flex, Form, InputNumber, Modal, Select, Spin, Tag, Typography } from "antd";
import { CompanyContext, UpdateCompanyContext } from "../../context/companyContext";
import {
  AssignInput,
  ContractPlanProvider,
  EffectiveLimits,
  toAssignPayload,
  useContractPlans,
} from "../../context/contractPlanContext";
import { useTranslation } from "react-i18next";

/** オーバーライド入力 5項目+ のフォーム値 (空欄=プラン値使用 → null)。 */
type OverrideFormValues = {
  rate_limit_per_minute_override: number | null;
  rate_limit_per_hour_override: number | null;
  rate_limit_image_per_minute_override: number | null;
  max_page_size_override: number | null;
  max_api_keys_per_company_override: number | null;
  max_redeliveries_per_day_override: number | null;
};

const EMPTY_OVERRIDES: OverrideFormValues = {
  rate_limit_per_minute_override: null,
  rate_limit_per_hour_override: null,
  rate_limit_image_per_minute_override: null,
  max_page_size_override: null,
  max_api_keys_per_company_override: null,
  max_redeliveries_per_day_override: null,
};

// min はバックエンド validateOverrideField の最小値に合わせる。
// max_page_size_override のみ min=1 (0 だと実効 maxPageSize=0 で外部APIのページング検証が破綻するため)。
// 他は min=0 (rate 系の 0 = 一時的な全停止が有効値)。
const OVERRIDE_FIELDS: { name: keyof OverrideFormValues; labelKey: string; min: number }[] = [
  { name: "rate_limit_per_minute_override", labelKey: "companyPlan.fields.rateLimitPerMinute", min: 0 },
  { name: "rate_limit_per_hour_override", labelKey: "companyPlan.fields.rateLimitPerHour", min: 0 },
  { name: "rate_limit_image_per_minute_override", labelKey: "companyPlan.fields.rateLimitImagePerMinute", min: 0 },
  { name: "max_page_size_override", labelKey: "companyPlan.fields.maxPageSize", min: 1 },
  { name: "max_api_keys_per_company_override", labelKey: "companyPlan.fields.maxApiKeysPerCompany", min: 0 },
  { name: "max_redeliveries_per_day_override", labelKey: "companyPlan.fields.maxRedeliveriesPerDay", min: 0 },
];

/** 実効上限の値を表示用文字列にする。 */
function renderLimitValue(v: unknown, t: (key: string) => string): string {
  if (v === null || v === undefined) return "-";
  if (typeof v === "boolean") return v ? t("companyPlan.allowed") : t("companyPlan.notAllowed");
  return String(v);
}

function CompanyPlanPageInner() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { companyOptions } = useContext(CompanyContext);
  const { refreshCompanyOptions } = useContext(UpdateCompanyContext);
  const { plans, listPlans, assignToCompany, getEffectiveLimits } = useContractPlans();

  const [companyId, setCompanyId] = useState<number | null>(null);
  const [effective, setEffective] = useState<EffectiveLimits | null>(null);
  const [loadingLimits, setLoadingLimits] = useState(false);

  const [form] = Form.useForm<OverrideFormValues>();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    refreshCompanyOptions();
    listPlans();
  }, [refreshCompanyOptions, listPlans]);

  const loadEffective = useCallback(
    async (cid: number) => {
      setLoadingLimits(true);
      try {
        const el = await getEffectiveLimits(cid);
        setEffective(el);
      } finally {
        setLoadingLimits(false);
      }
    },
    [getEffectiveLimits]
  );

  const onSelectCompany = useCallback(
    (cid: number) => {
      setCompanyId(cid);
      setSelectedPlanId(null);
      form.setFieldsValue(EMPTY_OVERRIDES);
      loadEffective(cid);
    },
    [form, loadEffective]
  );

  // 割当可能なプラン (有効なもののみドロップダウンに出す)
  const activePlanOptions = useMemo(
    () => plans.filter((p) => p.is_active).map((p) => ({ label: `${p.name} (${p.code})`, value: p.id })),
    [plans]
  );

  const effectiveEntries = useMemo(
    () => (effective ? Object.entries(effective.effective_limits) : []),
    [effective]
  );

  const doAssign = useCallback(
    async (payload: AssignInput) => {
      if (companyId == null) return;
      setSubmitting(true);
      try {
        const result = await assignToCompany(companyId, payload);
        if (!result.ok) {
          message.error(result.message);
          return;
        }
        message.success(t("companyPlan.applySucceeded"));
        // 成功後にプレビューを再取得
        loadEffective(companyId);
      } finally {
        setSubmitting(false);
      }
    },
    [companyId, assignToCompany, message, loadEffective, t]
  );

  const onApply = useCallback(async () => {
    if (companyId == null) {
      message.error(t("companyPlan.selectCompanyError"));
      return;
    }
    if (selectedPlanId == null) {
      message.error(t("companyPlan.selectPlanError"));
      return;
    }
    let values: OverrideFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = toAssignPayload(selectedPlanId, values);
    const planLabel = activePlanOptions.find((p) => p.value === selectedPlanId)?.label ?? String(selectedPlanId);

    Modal.confirm({
      title: t("companyPlan.confirmTitle"),
      content: t("companyPlan.confirmContent", { planLabel }),
      okText: t("companyPlan.apply"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => doAssign(payload),
    });
  }, [companyId, selectedPlanId, form, activePlanOptions, message, doAssign, t]);

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>{t("companyPlan.title")}</Typography.Title>

      <Flex align="center" gap="middle" wrap style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder={t("company.selectCompany")}
          style={{ minWidth: 280 }}
          optionFilterProp="label"
          value={companyId ?? undefined}
          onChange={onSelectCompany}
          options={companyOptions.map((c) => ({ label: c.company_name, value: c.id }))}
        />
      </Flex>

      {companyId != null && (
        <Spin spinning={loadingLimits}>
          {/* 現在の割当プラン + 適用中の実効上限プレビュー */}
          <Descriptions
            title={t("companyPlan.currentAssignment")}
            bordered
            column={1}
            size="small"
            style={{ marginBottom: 24, maxWidth: 640 }}
          >
            <Descriptions.Item label={t("companyPlan.assignedPlan")}>
              {effective?.plan_code ? <Tag color="blue">{effective.plan_code}</Tag> : t("companyPlan.unassigned")}
            </Descriptions.Item>
            {effectiveEntries.length > 0 ? (
              effectiveEntries.map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {renderLimitValue(value, t)}
                </Descriptions.Item>
              ))
            ) : (
              <Descriptions.Item label={t("companyPlan.effectiveLimits")}>-</Descriptions.Item>
            )}
          </Descriptions>

          <Typography.Title level={5}>{t("companyPlan.planChangeTitle")}</Typography.Title>
          <Alert type="info" showIcon message={t("companyPlan.overrideNote")} style={{ marginBottom: 16, maxWidth: 640 }} />

          <Flex vertical gap="small" style={{ maxWidth: 640, marginBottom: 16 }}>
            <div style={{ marginBottom: 4 }}>{t("companyPlan.applyPlanRequired")}</div>
            <Select
              placeholder={t("companyPlan.planSelectPlaceholder")}
              style={{ width: "100%" }}
              value={selectedPlanId ?? undefined}
              onChange={setSelectedPlanId}
              options={activePlanOptions}
            />
          </Flex>

          <Form form={form} layout="vertical" initialValues={EMPTY_OVERRIDES} style={{ maxWidth: 640 }}>
            <Flex gap="middle" wrap>
              {OVERRIDE_FIELDS.map((f) => (
                <Form.Item key={f.name} label={t(f.labelKey)} name={f.name} extra={t("companyPlan.blankUsesPlanValue")}>
                  <InputNumber min={f.min} precision={0} style={{ width: 180 }} placeholder={t("companyPlan.usePlanValue")} />
                </Form.Item>
              ))}
            </Flex>
          </Form>

          <Button type="primary" loading={submitting} onClick={onApply} disabled={selectedPlanId == null}>
            {t("companyPlan.apply")}
          </Button>
        </Spin>
      )}
    </div>
  );
}

export default function CompanyPlanPage() {
  return (
    <ContractPlanProvider>
      <CompanyPlanPageInner />
    </ContractPlanProvider>
  );
}
